<template>
  <section class="sent-view mt-5 mb-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 pb-3">
          <div class="sent-view__head">
            <div>
              <h1 class="sent-view__title mb-1">My Invitations</h1>
              <p class="sent-view__subtitle mb-0">Manage active, upcoming, past, and draft invitations.</p>
            </div>
            <router-link to="/create-invitation" class="sent-view__cta">
              <i class="fas fa-plus-circle"></i>
              <span>Add Invitation</span>
            </router-link>
          </div>
        </div>
        <div class="col-md-8">
          <!-- Skeleton Loaders -->
          <div v-if="loading" class="skeleton-container">
            <div v-for="section in 4" :key="`section-${section}`" class="skeleton-section">
              <div class="skeleton-title"></div>
              <div class="skeleton-cards">
                <div 
                  v-for="card in 3" 
                  :key="`card-${section}-${card}`" 
                  class="skeleton-card"
                >
                  <div class="skeleton-image"></div>
                  <div class="skeleton-content">
                    <div class="skeleton-event-title"></div>
                    <div class="skeleton-text"></div>
                    <div class="skeleton-text short"></div>
                    <div class="skeleton-meta">
                      <div class="skeleton-badge"></div>
                      <div class="skeleton-badge"></div>
                    </div>
                    <div class="skeleton-actions">
                      <div class="skeleton-button"></div>
                      <div class="skeleton-button"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actual Content -->
          <template v-else>
            <InvitationList title="Active Invitations"   :items="active"   type="active"   @delete="deleteInvitation" />
            <InvitationList title="Upcoming Invitations" :items="upcoming" type="upcoming" @delete="deleteInvitation" />
            <InvitationList title="Past Invitations"     :items="past"     type="past"     @delete="deleteInvitation" />
            <InvitationList title="Draft Invitations"    :items="draft"    type="draft"    @delete="deleteInvitation" />
          </template>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !active.length && !upcoming.length && !past.length && !draft.length" class="row mt-4">
        <div class="col-12 text-center py-5">
          <img v-no-right-click 
            src="/assets/placeholders/no-events.png" 
            alt="No events" 
            style="max-width: 240px;" 
            class="mb-3"
            @error="handleImageError"
          >  
          <p class="text-muted mb-3">You haven't created any invitations yet</p>
          <router-link to="/create-invitation" class="btn btn-primary">
            <i class="fas fa-plus-circle mr-2"></i>Create Your First Invitation
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/services/api' 
import InvitationList from '@/components/common/InvitationList.vue' 
import { useSessionStore } from '@/stores/session'
import { useHead } from '@vueuse/head'

const active = ref([])
const upcoming = ref([])
const past = ref([])
const draft = ref([])
const loading = ref(true)
const session = useSessionStore()

let loadingTimeout = null

useHead({
  title: 'My Invitations - Invi8',
  meta: [
    { name: 'description', content: 'Manage your event invitations - active, upcoming, past, and draft events.' },
    { name: 'keywords', content: 'invitations, events, invites, rsvp, event management' },
  ],
})

// Handle broken images
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  if (parent) {
    const icon = document.createElement('i')
    icon.className = 'fas fa-calendar-alt fa-3x mb-3 text-light'
    parent.insertBefore(icon, event.target.nextSibling)
  }
}

async function loadInvitations() {
  try {
    loading.value = true
    
    // Set minimum loading time for better UX
    loadingTimeout = setTimeout(() => {
      // Skeleton will show for at least 800ms
    }, 800)

    const formData = new FormData()
    formData.append('id', session.userId) 
    const response = await api.getInvitations(formData)
    
    if (response?.status) {
      active.value = response.data?.data || []
      upcoming.value = response.data?.upcomingdata || []
      past.value = response.data?.olddata || []
      draft.value = response.data?.draftdata || []
    } else {
      // Handle API response without status
      active.value = []
      upcoming.value = []
      past.value = []
      draft.value = []
    }
  } catch (err) {
    console.error('Error loading invitations:', err)
    // Set empty arrays on error
    active.value = []
    upcoming.value = []
    past.value = []
    draft.value = []
  } finally {
    loading.value = false
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
  }
}

async function deleteInvitation(event) {
  try {
    const formData = new FormData() 
    formData.append('id', event.id)
    await api.deleteInvitation(formData)
    await loadInvitations()
  } catch (err) {
    console.error('Delete failed:', err)
    // You might want to show a toast notification here
  }
}

onMounted(() => {
  loadInvitations()
})

// Cleanup timeout on component unmount
onMounted(() => {
  return () => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
  }
})
</script>

<style scoped>
.sent-view__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 2px 2px 0;
}

.sent-view__title {
  color: #2a1b22;
  font-size: 1.28rem;
  font-weight: 700;
}

.sent-view__subtitle {
  color: #8c6f7c;
  font-size: 0.92rem;
}

.sent-view__cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #f0c8d7;
  background: linear-gradient(90deg, #ff4d94 0%, #ff9e57 100%);
  color: #fff;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(255, 105, 155, 0.2);
}

.sent-view__cta:hover {
  color: #fff;
  text-decoration: none;
}

/* Skeleton Loader Styles */
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.skeleton-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-title {
  height: 24px;
  width: 200px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  margin-bottom: 0.5rem;
}

.skeleton-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-card {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  background: #fff;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-event-title {
  height: 20px;
  width: 70%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-text {
  height: 14px;
  width: 90%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.skeleton-badge {
  height: 20px;
  width: 60px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.skeleton-button {
  height: 32px;
  width: 80px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-card {
    flex-direction: column;
    text-align: center;
  }
  
  .skeleton-image {
    width: 100%;
    height: 120px;
    margin: 0 auto;
  }
  
  .skeleton-content {
    align-items: center;
  }
  
  .skeleton-event-title,
  .skeleton-text {
    width: 100%;
  }
  
  .skeleton-text.short {
    width: 80%;
  }
  
  .skeleton-actions {
    justify-content: center;
  }
}

/* Button styles */
.btn-primary {
  background-color: #FB7210;
  border-color: #FB7210;
}

.btn-primary:hover {
  background-color: #e05a00;
  border-color: #d45300;
}

.pb-3 {
  padding-bottom: 1rem;
}

.mt-5 {
  margin-top: 3rem;
}

.mb-5 {
  margin-bottom: 3rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

@media (max-width: 576px) {
  .sent-view__head {
    flex-direction: column;
    align-items: stretch;
  }

  .sent-view__cta {
    justify-content: center;
    width: 100%;
  }
}
</style>
